import {
    CheckCircleOutlined,
    RocketOutlined,
    StarOutlined
} from '@ant-design/icons';
import { Button, Col, Divider, message, Modal, Row, Space, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AiFillSafetyCertificate } from 'react-icons/ai';
import { FaChartLine, FaRegLightbulb } from 'react-icons/fa';
import { IoRocketSharp } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import './homStyle.css';
import { useAuth } from '../../../Hooks/useAuth';

const { Title, Paragraph, Text } = Typography;

interface PromotionModalProps {
    visible: boolean;
    onClose: () => void;
    userId?: string;
}

const PromotionModal: React.FC<PromotionModalProps> = ({ visible, onClose, userId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showConfetti, setShowConfetti] = useState(false);

    // Function to navigate to the premium services tab
    const goToPremiumServices = () => {
        setShowConfetti(true);
        setTimeout(() => {
            // Close the modal after confetti shows
            onClose();

            // Get the current user ID from either props or auth context
            const userIdToUse = userId || user?.id;

            if (userIdToUse) {
                // Navigate to user profile with the premium services tab selected
                navigate(`/profile/${userIdToUse}?tab=3`);
            } else {
                // If no user ID available, redirect to login
                message.error("Veuillez vous connecter pour accéder aux services premium");
                navigate('/login?redirect=premium-services');
            }
        }, 800);
    };

    const planFeatures = [
        {
            title: 'Standard',
            price: 'Gratuit',
            features: [
                '10 annonce active',
                'Visibilité de base',
                'Support par email',
                'Durée de 30 jours'
            ],
            recommended: false,
            color: 'bg-surface-secondary'
        },
        {
            title: 'Débutant',
            price: '5 500 CFA',
            features: [
                '15 annonces actives',
                'Visibilité premium',
                'Badge vendeur vérifié',
                'Support prioritaire',
                'Renouvellement auto des annonces/48h',
                'Durée de 60 jours'
            ],
            recommended: true,
            color: 'bg-gradient-to-br from-primary-secondary to-accent-secondary'
        },
        {
            title: 'Pro',
            price: '10 000 CFA',
            features: [
                'Annonces illimitées',
                'Visibilité maximale',
                'Badge vendeur pro',
                'Support dédié 24/7',
                'Annonces en avant',
                'Renouvellement auto des annonces/24h',
                'Durée de 90 jours'
            ],
            recommended: false,
            color: 'bg-gradient-to-br from-accent-secondary to-primary-secondary'
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            className="promotion-modal"
            centered
        >
            {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}

            <div className="bg-gradient-to-r from-primary-secondary via-accent-default to-accent-secondary -mt-6 -mx-6 px-6 py-10 rounded-t-lg relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

                {/* Animated circles */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary-secondary/20 rounded-full blur-xl"></div>
                <div className="absolute -bottom-16 -right-16 w-60 h-60 bg-accent-secondary/20 rounded-full blur-xl"></div>

                <Row align="middle" justify="center" className="relative z-10">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 100 }}
                    >
                        <IoRocketSharp style={{ fontSize: 40, color: 'white', marginRight: 16 }} />
                    </motion.div>
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Title level={2} style={{ color: 'white', margin: 0 }}>
                            Boostez vos ventes maintenant !
                        </Title>
                    </motion.div>
                </Row>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                >
                    <Paragraph className="text-center text-white/90 mt-3 max-w-lg mx-auto">
                        Rejoignez plus de 10 000 vendeurs qui ont augmenté leurs ventes grâce à nos plans premium.
                    </Paragraph>
                </motion.div>
            </div>

            <div className="px-4 py-3">
                {/* Pricing Plans */}
                <div className="my-4">
                    <Row gutter={[16, 16]} justify="center">
                        {planFeatures.map((plan, index) => (
                            <Col key={index} xs={24} md={8}>
                                <motion.div
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                                    className={`
                                        h-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all
                                        ${plan.recommended ? 'border-2 border-primary-default' : 'border border-surface-tertiary'}
                                    `}
                                >
                                    <div className={`${plan.color} py-4 px-4 relative`}>
                                        {plan.recommended && (
                                            <div className="absolute top-0 right-0">
                                                <div className="bg-primary-secondary text-light-default text-xs py-1 px-3 rounded-bl-lg font-bold">
                                                    POPULAIRE
                                                </div>
                                            </div>
                                        )}
                                        <h3 className={`text-lg font-bold mb-1 ${plan.recommended || plan.title === 'Business' ? 'text-light-default' : 'text-content-default'}`}>
                                            {plan.title}
                                        </h3>
                                        <div className={`text-2xl font-bold ${plan.recommended || plan.title === 'Business' ? 'text-light-default' : 'text-content-default'}`}>
                                            {plan.price}
                                            {plan.title !== 'Standard' && <span className="text-sm font-normal ml-1 text-content-secondary">/mois</span>}
                                        </div>
                                    </div>

                                    <div className="p-5 bg-light-default h-full">
                                        <ul className="space-y-3">
                                            {plan.features.map((feature, i) => (
                                                <motion.li
                                                    key={i}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{
                                                        opacity: 1,
                                                        x: 0,
                                                        transition: { delay: 0.5 + i * 0.1 }
                                                    }}
                                                    className="flex items-center text-content-default"
                                                >
                                                    <CheckCircleOutlined className="text-accent-default mr-2" />
                                                    <span className="text-sm">{feature}</span>
                                                </motion.li>
                                            ))}
                                        </ul>

                                        <div className="mt-6">
                                            <Button
                                                type={plan.recommended ? "primary" : "default"}
                                                disabled={plan.title === 'Standard'}
                                                block
                                                size="large"
                                                onClick={goToPremiumServices}
                                                className={plan.recommended ? "bg-gradient-to-r from-primary-secondary to-accent-secondary hover:from-primary-secondary hover:to-accent-secondary/90 shadow-md text-light-default" : "border border-primary-default text-primary-default hover:bg-primary-default hover:text-light-default"}
                                            >
                                                {plan.title === 'Standard' ? 'Utiliser gratuitement' : 'Choisir ce plan'}
                                            </Button>
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Call to action */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                >
                    <Row justify="center" gutter={16} className="mt-8">
                        <Col>
                            <Tooltip title="Voir nos différentes offres premium">
                                <Button
                                    type="primary"
                                    size="large"
                                    className="bg-gradient-to-r from-primary-secondary to-accent-secondary border-0 h-12 px-8 hover:from-primary-secondary hover:to-accent-secondary/90 text-light-default"
                                    onClick={goToPremiumServices}
                                >
                                    <span className="flex items-center">
                                        <StarOutlined className="mr-2" />
                                        Devenir Premium
                                    </span>
                                </Button>
                            </Tooltip>
                        </Col>
                        <Col>
                            <Link to="/sell">
                                <Button
                                    size="large"
                                    className="h-12 px-8 border border-primary-default text-primary-default hover:bg-primary-default hover:text-light-default transition"
                                    onClick={onClose}
                                >
                                    <span className="flex items-center">
                                        <RocketOutlined className="mr-2" />
                                        Vendre Maintenant
                                    </span>
                                </Button>
                            </Link>
                        </Col>
                    </Row>
                </motion.div>

                <Divider />

                {/* Feature highlights */}
                <Row gutter={[24, 24]} className="mb-8">
                    <Col span={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            whileHover={{ y: -5 }}
                            className="feature-card border rounded-lg p-5 h-full bg-light-default hover:border-primary-default"
                        >
                            <Space direction="vertical" size="middle" className="w-full">
                                <div className="flex items-center">
                                    <div className="p-2 rounded-full bg-primary-secondary/10 mr-3">
                                        <FaChartLine className="text-primary-secondary text-xl" />
                                    </div>
                                    <Text strong className="text-lg text-content-default">Visibilité accrue</Text>
                                </div>
                                <Paragraph className="text-content-secondary">
                                    Augmentez vos chances de vente de 300%. Vos annonces apparaissent en tête des résultats
                                    de recherche et sont mises en avant sur notre page d'accueil.
                                </Paragraph>
                            </Space>
                        </motion.div>
                    </Col>

                    <Col span={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 }}
                            whileHover={{ y: -5 }}
                            className="feature-card border rounded-lg p-5 h-full bg-light-default hover:border-primary-default"
                        >
                            <Space direction="vertical" size="middle" className="w-full">
                                <div className="flex items-center">
                                    <div className="p-2 rounded-full bg-accent-secondary/10 mr-3">
                                        <AiFillSafetyCertificate className="text-accent-secondary text-xl" />
                                    </div>
                                    <Text strong className="text-lg text-content-default">Badges premium</Text>
                                </div>
                                <Paragraph className="text-content-secondary">
                                    Inspirez confiance avec des badges exclusifs qui vous distinguent des autres vendeurs
                                    et augmentent votre crédibilité auprès des acheteurs.
                                </Paragraph>
                            </Space>
                        </motion.div>
                    </Col>
                </Row>

                {/* Success story */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 }}
                    className="bg-surface-secondary p-6 rounded-xl mb-6 relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-20 h-20 bg-primary-secondary/10 rounded-full blur-xl"></div>

                    <Row align="middle" gutter={16}>
                        <Col xs={24} sm={4}>
                            <div className="flex justify-center">
                                <FaRegLightbulb className="text-primary-secondary text-4xl" />
                            </div>
                        </Col>
                        <Col xs={24} sm={20}>
                            <div>
                                <Text strong className="text-lg block mb-1 text-content-default">Le saviez-vous ?</Text>
                                <Paragraph className="text-content-secondary mb-0">
                                    Les vendeurs premium réalisent en moyenne <span className="font-bold text-primary-default">70% de ventes supplémentaires</span> et
                                    concluent leurs transactions <span className="font-bold text-primary-default">3x plus rapidement</span> que les comptes standards.
                                </Paragraph>
                            </div>
                        </Col>
                    </Row>
                </motion.div>

            </div>
        </Modal>
    );
};

export default PromotionModal;
